/**
 * 後台 — 叫貨出貨(叫貨與出貨模組總覽)
 *
 * 霽雲每月固定三個下單日(預設 5 / 15 / 25),每個下單日是一「批」(批次 id = 下單日 YYYY-MM-DD)。
 * 下單日往後推:工廠出貨日 → 到貨日 → 出給會員日(工作天,扣掉不出貨日期)。
 *   批次     — 原料結餘、下一個下單日、各批狀態與原料夠不夠;點一列進批次頁(下單 / 到貨 / 出貨)
 *   會員配送 — 每位會員「每批出幾瓶」(即飲 / 鮮燉),批次名單依這份產生
 *   原料帳   — 工廠原料結餘(克):進貨、盤點、下單自動扣用的明細
 *   規則設定 — 下單日、交期、每瓶克數、緩衝、不出貨日期、下單前一天推播提醒
 * 資料來源 /api/admin/supply/*;批次明細在 SupplyBatch.tsx。
 *
 * Route: /admin/supply(?tab=plans|raw|settings)
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import AdminHeader from "../../components/AdminHeader";
import DataTable, { type Column } from "../../components/DataTable";
import MemberSearchBox from "../../components/MemberSearchBox";
import SupplyStatusPill from "../../components/SupplyStatusPill";
import {
  adminSupplyDeletePlan,
  adminSupplyGetRaw,
  adminSupplyGetSettings,
  adminSupplyListPlans,
  adminSupplyOverview,
  adminSupplyRecordRaw,
  adminSupplyReminderPreview,
  adminSupplyReminderTest,
  adminSupplySavePlan,
  adminSupplyUpdateSettings,
  CARRIER_OPTIONS,
  type SupplyBatchSummary,
  type SupplyOverview,
  type SupplyPlanRow,
  type SupplyRaw,
  type SupplyRawLedgerRow,
  type SupplyReminderSummary,
  type SupplySettings,
} from "../../api/admin";
import type { Member } from "../../api/members";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";
import { todayTpe } from "../../utils/date";
import {
  daysBetween,
  fmtMD,
  fmtMDWeek,
  fmtNum,
  parseQty,
  qtyText,
  supplyErrorText,
  weekdayLabel,
} from "../../utils/supply";


type TabKey = "batches" | "plans" | "raw" | "settings";

const TABS: { key: TabKey; label: string }[] = [
  { key: "batches", label: "批次" },
  { key: "plans", label: "會員配送" },
  { key: "raw", label: "原料帳" },
  { key: "settings", label: "規則設定" },
];

const RAW_RANGE_HINT = "輸入 0 以上的數字,最多一位小數";


export default function Supply() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const [params, setParams] = useSearchParams();

  const rawTab = params.get("tab") || "";
  const tab: TabKey = TABS.some((t) => t.key === rawTab) ? (rawTab as TabKey) : "batches";

  useEffect(() => {
    if (role !== "admin") navigate("/admin/login", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  function changeTab(k: TabKey) {
    setParams(k === "batches" ? {} : { tab: k }, { replace: true });
  }

  if (role !== "admin") return null;

  return (
    <div className="min-h-screen bg-cream pb-12">
      <AdminHeader active="supply" />

      <div className="p-6 space-y-5">
        <div className="overflow-x-auto">
          <div className="flex gap-1 bg-white rounded-lg p-1 border border-line w-fit">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => changeTab(t.key)}
                className={`text-sm px-4 py-1.5 rounded whitespace-nowrap transition ${
                  tab === t.key ? "bg-primary text-white" : "text-muted hover:bg-cream"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {tab === "batches" && <BatchesTab onGoTab={changeTab} />}
        {tab === "plans" && <PlansTab />}
        {tab === "raw" && <RawTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}


// ── 批次 ──────────────────────────────────

function BatchesTab({ onGoTab }: { onGoTab: (k: TabKey) => void }) {
  const navigate = useNavigate();
  const showToast = useUIStore((s) => s.showToast);
  const [data, setData] = useState<SupplyOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminSupplyOverview();
        if (!cancelled) setData(r);
      } catch {
        if (!cancelled) showToast("叫貨總覽載入失敗", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) {
    return <div className="p-10 text-center text-muted">{loading ? "載入中…" : "無資料"}</div>;
  }

  const balance = data.raw.balance_grams;
  const nextDays = data.next_order_date ? daysBetween(data.as_of, data.next_order_date) : null;
  // 還沒下單、原料推算不夠的批次
  const shortBatches = data.batches.filter((b) => b.ordered === null && b.raw_check?.status === "short");

  const columns: Column<SupplyBatchSummary>[] = [
    {
      key: "order",
      header: "下單日",
      render: (b) => <span className="whitespace-nowrap font-medium text-ink">{fmtMDWeek(b.order_date)}</span>,
    },
    {
      key: "ship",
      header: "會員出貨日",
      render: (b) => <span className="whitespace-nowrap">{fmtMDWeek(b.ship_date)}</span>,
    },
    { key: "status", header: "狀態", render: (b) => <SupplyStatusPill status={b.status} label={b.status_label} /> },
    { key: "lines", header: "名單人數", render: (b) => `${b.line_count} 位` },
    {
      key: "suggested",
      header: "建議下單",
      render: (b) => <span className="whitespace-nowrap">{qtyText(b.suggested)}</span>,
    },
    {
      key: "ordered",
      header: "實際下單",
      render: (b) => <span className="whitespace-nowrap">{b.ordered ? qtyText(b.ordered) : "—"}</span>,
    },
    { key: "raw", header: "原料", render: (b) => <RawCheckText b={b} /> },
    {
      key: "warn",
      header: "注意",
      render: (b) => {
        const items: string[] = [];
        if (b.wallet_short_count > 0) items.push(`存摺不足 ${b.wallet_short_count}`);
        if (b.missing_address_count > 0) items.push(`缺地址 ${b.missing_address_count}`);
        if (b.inactive_member_count > 0) items.push(`會員停用 ${b.inactive_member_count}`);
        if (b.ship_unknown_count > 0) items.push(`出貨狀態不明 ${b.ship_unknown_count}`);
        if (items.length === 0) return <span className="text-muted">—</span>;
        return <span className="text-coral whitespace-nowrap">{items.join("、")}</span>;
      },
    },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card">
          <div className="text-xs text-muted">原料結餘</div>
          <div className="flex items-baseline gap-1 mt-1">
            <span className={`text-2xl font-bold ${balance < 0 ? "text-coral" : "text-ink"}`}>{fmtNum(balance)}</span>
            <span className="text-xs text-muted">克</span>
          </div>
          <button onClick={() => onGoTab("raw")} className="text-xs text-primary hover:underline mt-1">
            查看原料帳
          </button>
        </div>
        <div className="card">
          <div className="text-xs text-muted">下一個下單日</div>
          {data.next_order_date ? (
            <>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-2xl font-bold text-ink">{fmtMD(data.next_order_date)}</span>
                <span className="text-sm text-muted">{weekdayLabel(data.next_order_date)}</span>
              </div>
              <div className="text-xs text-muted mt-1">
                {nextDays === 0 ? "就是今天" : nextDays !== null && nextDays > 0 ? `還有 ${nextDays} 天` : "已過下單日,尚未確認下單"}
              </div>
            </>
          ) : (
            <div className="text-sm text-muted mt-2">目前沒有待下單的批次</div>
          )}
        </div>
        <div className="card">
          <div className="text-xs text-muted">配送會員</div>
          <div className="flex items-baseline gap-1 mt-1">
            <span className="text-2xl font-bold text-ink">{data.plans.members}</span>
            <span className="text-xs text-muted">位</span>
          </div>
          <div className="text-xs text-muted mt-1">
            每批即飲 {data.plans.drink} / 鮮燉 {data.plans.fresh} 瓶
          </div>
        </div>
      </div>

      {shortBatches.length > 0 && (
        <div className="border border-coral/40 bg-coral/5 rounded-lg p-3 space-y-1">
          {/* 原料依批次往後扣,第一批不夠之後每批都不夠;只講從哪一批開始,加上到最後一批累計還差多少 */}
          <div className="text-sm text-coral">
            {fmtMD(shortBatches[0].order_date, true)} 那批起原料不足:這批差 {fmtNum(shortBatches[0].raw_check?.short_grams)} 克
            {shortBatches.length > 1 &&
              `,到 ${fmtMD(shortBatches[shortBatches.length - 1].order_date, true)} 累計差 ${fmtNum(
                shortBatches[shortBatches.length - 1].raw_check?.short_grams,
              )} 克`}
          </div>
          <div className="text-xs text-muted pt-1">到原料帳登記進貨或盤點,結餘就會補上。</div>
        </div>
      )}

      <div className="card">
        <div className="text-sm font-bold text-ink mb-3">
          待處理的批次
          <span className="ml-2 text-xs font-normal text-muted">點一列進入批次頁</span>
        </div>
        <DataTable
          columns={columns}
          rows={data.batches}
          rowKey={(b) => b.batch_id}
          onRowClick={(b) => navigate(`/admin/supply/batches/${b.batch_id}`)}
          emptyHint="目前沒有待處理的批次"
        />
      </div>

      <div className="card">
        <div className="text-sm font-bold text-ink mb-3">最近完成的批次</div>
        {data.recent.length === 0 ? (
          <div className="text-sm text-muted">還沒有完成的批次</div>
        ) : (
          <div>
            {data.recent.map((b) => (
              <button
                key={b.batch_id}
                onClick={() => navigate(`/admin/supply/batches/${b.batch_id}`)}
                className="w-full text-left flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2 border-b border-line/60 last:border-0 hover:bg-cream/50 px-2 -mx-2 rounded text-sm"
              >
                <span className="text-ink font-medium whitespace-nowrap">{fmtMD(b.order_date, true)} 下單</span>
                <span className="text-muted whitespace-nowrap">會員出貨 {fmtMD(b.ship_date, true)}</span>
                <span className="text-muted whitespace-nowrap">名單 {b.line_count} 位</span>
                <span className="text-muted whitespace-nowrap">下單 {qtyText(b.ordered)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RawCheckText({ b }: { b: SupplyBatchSummary }) {
  const c = b.raw_check;
  if (!c) return <span className="text-muted">—</span>;
  if (c.status === "deducted") {
    return <span className="whitespace-nowrap text-muted">已扣 {fmtNum(b.raw_grams_used)} 克</span>;
  }
  if (c.status === "short") {
    return <span className="whitespace-nowrap text-coral">不足 差 {fmtNum(c.short_grams)} 克</span>;
  }
  return <span className="whitespace-nowrap text-primary">足夠</span>;
}


// ── 會員配送 ──────────────────────────────

interface PlanForm {
  uid: string;
  name: string;
  phone: string;
  isNew: boolean;
  drink: string;
  fresh: string;
  start_date: string;
  active: boolean;
  note: string;
}

function PlansTab() {
  const showToast = useUIStore((s) => s.showToast);
  const [rows, setRows] = useState<SupplyPlanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<PlanForm | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await adminSupplyListPlans();
      setRows(r.plans);
    } catch {
      showToast("會員配送設定載入失敗", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function formFromRow(r: SupplyPlanRow): PlanForm {
    return {
      uid: r.member_uid,
      name: r.name || r.display_name || r.member_uid,
      phone: r.phone,
      isNew: false,
      drink: String(r.drink),
      fresh: String(r.fresh),
      start_date: r.start_date || "",
      active: r.active,
      note: r.note || "",
    };
  }

  function pickMember(m: Member) {
    const existing = rows.find((r) => r.member_uid === m.line_user_id);
    setAdding(false);
    if (existing) {
      showToast("這位會員已有配送設定,已帶入現有內容", "info");
      setForm(formFromRow(existing));
      return;
    }
    setForm({
      uid: m.line_user_id,
      name: m.name || m.display_name || "—",
      phone: m.phone || "",
      isNew: true,
      drink: "0",
      fresh: "0",
      start_date: "",
      active: true,
      note: "",
    });
  }

  async function save() {
    if (!form) return;
    const drink = parseQty(form.drink);
    const fresh = parseQty(form.fresh);
    if (drink === null || fresh === null) {
      showToast("每批瓶數請填 0 以上的整數", "error");
      return;
    }
    setBusy(true);
    try {
      const r = await adminSupplySavePlan(form.uid, {
        drink,
        fresh,
        active: form.active,
        start_date: form.start_date,
        note: form.note.trim(),
      });
      if (r.status === "ok") {
        showToast("配送設定已儲存", "success");
        setForm(null);
        await load();
      } else {
        showToast(supplyErrorText(r), "error");
      }
    } catch {
      showToast("儲存失敗,請稍後再試", "error");
    } finally {
      setBusy(false);
    }
  }

  async function remove(r: SupplyPlanRow) {
    const name = r.name || r.display_name || r.member_uid;
    if (!confirm(`確定刪除「${name}」的配送設定?之後的批次不會再排入這位會員。`)) return;
    try {
      const res = await adminSupplyDeletePlan(r.member_uid);
      if (res.status === "ok") {
        showToast("已刪除配送設定", "success");
        if (form?.uid === r.member_uid) setForm(null);
        await load();
      } else {
        showToast(supplyErrorText(res), "error");
      }
    } catch {
      showToast("刪除失敗,請稍後再試", "error");
    }
  }

  const columns: Column<SupplyPlanRow>[] = [
    {
      key: "name",
      header: "會員",
      render: (r) => (
        <div className="whitespace-nowrap">
          <div className="font-medium text-ink">{r.name || "—"}</div>
          {r.display_name && <div className="text-xs text-muted">{r.display_name}</div>}
        </div>
      ),
    },
    { key: "phone", header: "電話", render: (r) => r.phone || "—" },
    {
      key: "address",
      header: "地址",
      render: (r) =>
        r.address_ok ? (
          <span className="text-sm">{r.address_text}</span>
        ) : (
          <span className="text-coral whitespace-nowrap">缺地址</span>
        ),
    },
    { key: "drink", header: "每批即飲", render: (r) => r.drink },
    { key: "fresh", header: "每批鮮燉", render: (r) => r.fresh },
    {
      key: "wallet",
      header: "存摺 即飲 / 鮮燉",
      render: (r) => <span className="whitespace-nowrap">{r.wallet.drink} / {r.wallet.fresh}</span>,
    },
    {
      key: "start",
      header: "首次出貨不早於",
      render: (r) => (r.start_date ? <span className="whitespace-nowrap">{r.start_date}</span> : "—"),
    },
    { key: "status", header: "狀態", render: (r) => <PlanStatus r={r} /> },
    { key: "note", header: "備註", render: (r) => r.note || "—" },
    {
      key: "ops",
      header: "",
      render: (r) => (
        <div className="flex items-center gap-3 whitespace-nowrap">
          <button
            onClick={() => setForm(formFromRow(r))}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <Pencil size={13} /> 編輯
          </button>
          <button
            onClick={() => remove(r)}
            className="inline-flex items-center gap-1 text-xs text-coral hover:underline"
          >
            <Trash2 size={13} /> 刪除
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-muted">
          每位會員「每批出幾瓶」;批次名單依這份設定產生。暫停配送、會員停用或還沒到首次出貨日的,不會排進批次。
        </div>
        {!adding && !form && (
          <button onClick={() => setAdding(true)} className="btn-primary inline-flex items-center gap-1.5 text-sm py-2">
            <Plus size={16} /> 新增會員
          </button>
        )}
      </div>

      {adding && (
        <div className="card max-w-xl">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium">選擇要加入配送的會員</div>
            <button onClick={() => setAdding(false)} className="text-xs text-muted hover:underline">取消</button>
          </div>
          <MemberSearchBox onPick={pickMember} autoFocus />
        </div>
      )}

      {form && (
        <div className="card max-w-xl space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-sm font-bold text-ink">
                {form.isNew ? "新增配送會員" : "編輯配送設定"}:{form.name}
              </div>
              <div className="text-xs text-muted mt-0.5">{form.phone || "—"}</div>
            </div>
            <button onClick={() => setForm(null)} className="text-muted hover:text-ink" aria-label="關閉">
              <X size={18} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">每批即飲(瓶)</label>
              <input
                className="input"
                type="number"
                min={0}
                step={1}
                value={form.drink}
                onChange={(e) => setForm({ ...form, drink: e.target.value })}
              />
            </div>
            <div>
              <label className="label">每批鮮燉(瓶)</label>
              <input
                className="input"
                type="number"
                min={0}
                step={1}
                value={form.fresh}
                onChange={(e) => setForm({ ...form, fresh: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="label">首次出貨不早於(選填)</label>
            <input
              className="input"
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            />
            <div className="text-xxs text-muted mt-1">會員出貨日早於這一天的批次,不會排入這位會員。</div>
          </div>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            配送中(取消勾選 = 暫停配送)
          </label>
          <div>
            <label className="label">備註(選填)</label>
            <input
              className="input"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              maxLength={200}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setForm(null)} className="btn-secondary text-sm py-2" disabled={busy}>取消</button>
            <button onClick={save} className="btn-primary text-sm py-2" disabled={busy}>
              {busy ? "儲存中…" : "儲存"}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="text-sm font-bold text-ink mb-3">
          配送會員
          <span className="ml-2 text-xs font-normal text-muted">共 {rows.length} 位</span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-muted text-sm">載入中…</div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(r) => r.member_uid}
            emptyHint="還沒有設定配送會員,按右上「新增會員」開始"
          />
        )}
      </div>
    </div>
  );
}

function PlanStatus({ r }: { r: SupplyPlanRow }) {
  if (r.member_status === "suspended" || r.member_status === "merged") {
    return <span className="text-coral whitespace-nowrap">會員已停用</span>;
  }
  if (r.member_status === "missing") {
    return <span className="text-coral whitespace-nowrap">會員不存在</span>;
  }
  if (!r.active) return <span className="text-muted whitespace-nowrap">暫停</span>;
  return <span className="text-primary whitespace-nowrap">配送中</span>;
}


// ── 原料帳 ────────────────────────────────

function RawTab() {
  const navigate = useNavigate();
  const showToast = useUIStore((s) => s.showToast);
  const [raw, setRaw] = useState<SupplyRaw | null>(null);
  const [loading, setLoading] = useState(true);

  const [buyDate, setBuyDate] = useState(() => todayTpe());
  const [buyGrams, setBuyGrams] = useState("");
  const [buyNote, setBuyNote] = useState("");
  const [countDate, setCountDate] = useState(() => todayTpe());
  const [countGrams, setCountGrams] = useState("");
  const [countNote, setCountNote] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      setRaw(await adminSupplyGetRaw());
    } catch {
      showToast("原料帳載入失敗", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 克數:大於等於 0、最多一位小數;不合法回 null
  function parseGrams(text: string): number | null {
    const t = text.trim();
    if (!/^\d+(\.\d)?$/.test(t)) return null;
    return Number(t);
  }

  async function purchase() {
    const grams = parseGrams(buyGrams);
    if (grams === null || grams <= 0) {
      showToast(`進貨克數請填大於 0 的數字(${RAW_RANGE_HINT})`, "error");
      return;
    }
    setBusy(true);
    try {
      const r = await adminSupplyRecordRaw({ type: "purchase", grams, date: buyDate, note: buyNote.trim() });
      if (r.status === "ok") {
        showToast(`已登記進貨,目前結餘 ${fmtNum(r.balance_grams)} 克`, "success");
        setBuyGrams("");
        setBuyNote("");
        await load();
      } else {
        showToast(supplyErrorText(r), "error");
      }
    } catch {
      showToast("登記失敗,請稍後再試", "error");
    } finally {
      setBusy(false);
    }
  }

  async function count() {
    const grams = parseGrams(countGrams);
    if (grams === null) {
      showToast(`實際結餘請填克數(${RAW_RANGE_HINT})`, "error");
      return;
    }
    const cur = raw?.balance_grams ?? 0;
    if (!confirm(`帳上結餘 ${fmtNum(cur)} 克,盤點後改為 ${fmtNum(grams)} 克(差額 ${fmtNum(Math.round((grams - cur) * 10) / 10)} 克)。確定?`)) {
      return;
    }
    setBusy(true);
    try {
      const r = await adminSupplyRecordRaw({ type: "count", balance_grams: grams, date: countDate, note: countNote.trim() });
      if (r.status === "ok") {
        showToast(`已登記盤點,目前結餘 ${fmtNum(r.balance_grams)} 克`, "success");
        setCountGrams("");
        setCountNote("");
        await load();
      } else {
        showToast(supplyErrorText(r), "error");
      }
    } catch {
      showToast("登記失敗,請稍後再試", "error");
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<SupplyRawLedgerRow>[] = [
    { key: "date", header: "日期", render: (r) => <span className="whitespace-nowrap">{r.entry_date || "—"}</span> },
    { key: "type", header: "類型", render: (r) => <span className="whitespace-nowrap">{r.type_label || r.type}</span> },
    {
      key: "grams",
      header: "克數",
      render: (r) => (
        <span className={`whitespace-nowrap font-medium ${r.grams < 0 ? "text-coral" : "text-primary"}`}>
          {r.grams > 0 ? "+" : r.grams < 0 ? "−" : ""}{fmtNum(Math.abs(r.grams))}
        </span>
      ),
    },
    {
      key: "balance",
      header: "結餘",
      render: (r) => (
        <span className={`whitespace-nowrap ${r.balance_after < 0 ? "text-coral" : ""}`}>{fmtNum(r.balance_after)}</span>
      ),
    },
    {
      key: "batch",
      header: "批次",
      render: (r) =>
        r.batch_id ? (
          <button
            onClick={() => navigate(`/admin/supply/batches/${r.batch_id}`)}
            className="text-primary hover:underline whitespace-nowrap"
          >
            {fmtMD(r.batch_id, true)} 批
          </button>
        ) : (
          "—"
        ),
    },
    { key: "note", header: "備註", render: (r) => r.note || "—" },
    { key: "operator", header: "經手人", render: (r) => <span className="text-xs text-muted">{r.operator || "—"}</span> },
  ];

  if (!raw) {
    return <div className="p-10 text-center text-muted">{loading ? "載入中…" : "無資料"}</div>;
  }

  return (
    <div className="space-y-5">
      <div className="card max-w-xs">
        <div className="text-xs text-muted">原料結餘</div>
        <div className="flex items-baseline gap-1 mt-1">
          <span className={`text-2xl font-bold ${raw.balance_grams < 0 ? "text-coral" : "text-ink"}`}>
            {fmtNum(raw.balance_grams)}
          </span>
          <span className="text-xs text-muted">克</span>
        </div>
        <div className="text-xxs text-muted mt-1">確認下單時依瓶數自動扣用;結餘為負代表原料還沒補上。</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card space-y-3">
          <div className="text-sm font-bold text-ink">原料進貨</div>
          <div>
            <label className="label">日期</label>
            <input className="input" type="date" value={buyDate} onChange={(e) => setBuyDate(e.target.value)} />
          </div>
          <div>
            <label className="label">進貨克數</label>
            <input
              className="input"
              type="number"
              min={0}
              step={0.1}
              value={buyGrams}
              onChange={(e) => setBuyGrams(e.target.value)}
              placeholder="例如 5000"
            />
          </div>
          <div>
            <label className="label">備註(選填)</label>
            <input className="input" value={buyNote} onChange={(e) => setBuyNote(e.target.value)} />
          </div>
          <button onClick={purchase} className="btn-primary text-sm py-2" disabled={busy}>登記進貨</button>
        </div>

        <div className="card space-y-3">
          <div className="text-sm font-bold text-ink">盤點</div>
          <div>
            <label className="label">日期</label>
            <input className="input" type="date" value={countDate} onChange={(e) => setCountDate(e.target.value)} />
          </div>
          <div>
            <label className="label">實際結餘克數</label>
            <input
              className="input"
              type="number"
              min={0}
              step={0.1}
              value={countGrams}
              onChange={(e) => setCountGrams(e.target.value)}
              placeholder="現場實際秤出的克數"
            />
            <div className="text-xxs text-muted mt-1">填實際結餘,系統會記下與帳上的差額。</div>
          </div>
          <div>
            <label className="label">備註(選填)</label>
            <input className="input" value={countNote} onChange={(e) => setCountNote(e.target.value)} />
          </div>
          <button onClick={count} className="btn-secondary text-sm py-2" disabled={busy}>登記盤點</button>
        </div>
      </div>

      <div className="card">
        <div className="text-sm font-bold text-ink mb-3">
          原料明細
          <span className="ml-2 text-xs font-normal text-muted">新的在前</span>
        </div>
        <DataTable
          columns={columns}
          rows={raw.ledger}
          rowKey={(r, i) => `${r.created_at || ""}-${i}`}
          emptyHint="還沒有原料紀錄,先登記一筆進貨或盤點"
        />
        {raw.ledger.length >= 200 && (
          <div className="text-xxs text-muted mt-3">只顯示最近 200 筆。</div>
        )}
      </div>
    </div>
  );
}


// ── 規則設定 ──────────────────────────────

interface SettingsForm {
  order_days: string[];
  lead: string;
  arrival: string;
  ship: string;
  gDrink: string;
  gFresh: string;
  bDrink: string;
  bFresh: string;
  holidays: string[];
  reminder: boolean;
  carrier: string;
}

function toForm(s: SupplySettings): SettingsForm {
  return {
    order_days: s.order_days.map(String),
    lead: String(s.lead_working_days),
    arrival: String(s.arrival_working_days),
    ship: String(s.ship_working_days),
    gDrink: String(s.grams_per_bottle.drink),
    gFresh: String(s.grams_per_bottle.fresh),
    bDrink: String(s.buffer_bottles.drink),
    bFresh: String(s.buffer_bottles.fresh),
    holidays: [...s.holidays].sort(),
    reminder: s.reminder_enabled,
    carrier: s.default_carrier || "",
  };
}

function intInRange(text: string, lo: number, hi: number): number | null {
  const t = text.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return n >= lo && n <= hi ? n : null;
}

function gramsInRange(text: string): number | null {
  const t = text.trim();
  if (!/^\d+(\.\d)?$/.test(t)) return null;
  const n = Number(t);
  return n >= 0 && n <= 1000 ? n : null;
}

function SettingsTab() {
  const showToast = useUIStore((s) => s.showToast);
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [holidayInput, setHolidayInput] = useState("");

  const [preview, setPreview] = useState<SupplyReminderSummary | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pushing, setPushing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await adminSupplyGetSettings();
        if (!cancelled) setForm(toForm(s));
      } catch {
        if (!cancelled) showToast("規則設定載入失敗", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!form) {
    return <div className="p-10 text-center text-muted">{loading ? "載入中…" : "無資料"}</div>;
  }

  function patch(p: Partial<SettingsForm>) {
    setForm((f) => (f ? { ...f, ...p } : f));
  }

  function setOrderDay(i: number, v: string) {
    patch({ order_days: form!.order_days.map((d, idx) => (idx === i ? v : d)) });
  }

  function addHoliday() {
    const d = holidayInput.trim();
    if (!d) {
      showToast("請先選一個日期", "error");
      return;
    }
    if (form!.holidays.includes(d)) {
      showToast("這個日期已在清單中", "info");
      return;
    }
    patch({ holidays: [...form!.holidays, d].sort() });
    setHolidayInput("");
  }

  async function save() {
    const f = form!;
    const days = f.order_days.map((d) => intInRange(d, 1, 31));
    if (days.length < 1 || days.length > 6 || days.some((d) => d === null)) {
      showToast("下單日請填 1 到 31 日,最少 1 個、最多 6 個", "error");
      return;
    }
    const orderDays = Array.from(new Set(days as number[])).sort((a, b) => a - b);
    const lead = intInRange(f.lead, 0, 60);
    const arrival = intInRange(f.arrival, 0, 10);
    const ship = intInRange(f.ship, 0, 10);
    if (lead === null) return showToast("交期工作天請填 0 到 60", "error");
    if (arrival === null) return showToast("工廠出貨到到貨工作天請填 0 到 10", "error");
    if (ship === null) return showToast("到貨到出貨工作天請填 0 到 10", "error");
    const gDrink = gramsInRange(f.gDrink);
    const gFresh = gramsInRange(f.gFresh);
    if (gDrink === null || gFresh === null) {
      return showToast("每瓶原料克數請填 0 到 1000,最多一位小數", "error");
    }
    const bDrink = intInRange(f.bDrink, 0, 100000);
    const bFresh = intInRange(f.bFresh, 0, 100000);
    if (bDrink === null || bFresh === null) {
      return showToast("緩衝瓶數請填 0 以上的整數", "error");
    }

    setBusy(true);
    try {
      const r = await adminSupplyUpdateSettings({
        order_days: orderDays,
        lead_working_days: lead,
        arrival_working_days: arrival,
        ship_working_days: ship,
        grams_per_bottle: { drink: gDrink, fresh: gFresh },
        buffer_bottles: { drink: bDrink, fresh: bFresh },
        holidays: f.holidays,
        reminder_enabled: f.reminder,
        default_carrier: f.carrier,
      });
      if (r.status === "ok" && r.settings) {
        setForm(toForm(r.settings));
        showToast("規則設定已儲存", "success");
      } else {
        showToast(supplyErrorText(r), "error");
      }
    } catch {
      showToast("儲存失敗,請稍後再試", "error");
    } finally {
      setBusy(false);
    }
  }

  async function loadPreview() {
    setPreviewLoading(true);
    try {
      const r = await adminSupplyReminderPreview();
      if (r.status === "ok" && r.summary) {
        setPreview(r.summary);
      } else {
        setPreview(null);
        showToast(r.status === "not_found" ? "目前沒有可預覽的批次" : supplyErrorText(r), "error");
      }
    } catch {
      showToast("載入預覽失敗", "error");
    } finally {
      setPreviewLoading(false);
    }
  }

  async function pushTest() {
    if (!confirm("會立刻推一則下單提醒卡到管理員 LINE 群組(測試用,不影響明天的排程提醒)。確定?")) return;
    setPushing(true);
    try {
      const r = await adminSupplyReminderTest();
      if (r.status === "ok") {
        showToast("已推一則測試提醒到管理群", "success");
      } else {
        showToast(supplyErrorText(r), "error");
      }
    } catch {
      showToast("推播失敗,請稍後再試", "error");
    } finally {
      setPushing(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-5">
      <section className="card space-y-3">
        <div>
          <div className="text-sm font-bold text-ink">下單日</div>
          <div className="text-xxs text-muted mt-0.5">每月固定向工廠下單的日子(1 到 31 日,最多 6 個)。當月沒有那一天(例如 31 日)時,以當月最後一天為準。</div>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          {form.order_days.map((d, i) => (
            <div key={i} className="flex items-center gap-1">
              <input
                className="input w-20 text-center"
                type="number"
                min={1}
                max={31}
                value={d}
                onChange={(e) => setOrderDay(i, e.target.value)}
              />
              <span className="text-sm text-muted">日</span>
              <button
                onClick={() => patch({ order_days: form.order_days.filter((_, idx) => idx !== i) })}
                disabled={form.order_days.length <= 1}
                className="text-muted hover:text-coral disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="移除這個下單日"
              >
                <X size={16} />
              </button>
            </div>
          ))}
          {form.order_days.length < 6 && (
            <button
              onClick={() => patch({ order_days: [...form.order_days, ""] })}
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              <Plus size={14} /> 新增下單日
            </button>
          )}
        </div>
      </section>

      <section className="card space-y-3">
        <div>
          <div className="text-sm font-bold text-ink">交期(工作天)</div>
          <div className="text-xxs text-muted mt-0.5">工作天 = 週一到週五,扣掉下方「不出貨日期」。</div>
        </div>
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="label">下單到工廠出貨(交期)</label>
            <input className="input w-28" type="number" min={0} max={60} value={form.lead} onChange={(e) => patch({ lead: e.target.value })} />
          </div>
          <div>
            <label className="label">工廠出貨到到貨</label>
            <input className="input w-28" type="number" min={0} max={10} value={form.arrival} onChange={(e) => patch({ arrival: e.target.value })} />
          </div>
          <div>
            <label className="label">到貨到出給會員</label>
            <input className="input w-28" type="number" min={0} max={10} value={form.ship} onChange={(e) => patch({ ship: e.target.value })} />
          </div>
        </div>
      </section>

      <section className="card space-y-3">
        <div>
          <div className="text-sm font-bold text-ink">原料與緩衝</div>
          <div className="text-xxs text-muted mt-0.5">每瓶用多少克原料,下單時依瓶數自動扣;緩衝瓶數是每批建議下單量預設多加的瓶數。</div>
        </div>
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="label">每瓶原料克數:即飲</label>
            <input className="input w-28" type="number" min={0} max={1000} step={0.1} value={form.gDrink} onChange={(e) => patch({ gDrink: e.target.value })} />
          </div>
          <div>
            <label className="label">每瓶原料克數:鮮燉</label>
            <input className="input w-28" type="number" min={0} max={1000} step={0.1} value={form.gFresh} onChange={(e) => patch({ gFresh: e.target.value })} />
          </div>
          <div>
            <label className="label">每批緩衝瓶數:即飲</label>
            <input className="input w-28" type="number" min={0} step={1} value={form.bDrink} onChange={(e) => patch({ bDrink: e.target.value })} />
          </div>
          <div>
            <label className="label">每批緩衝瓶數:鮮燉</label>
            <input className="input w-28" type="number" min={0} step={1} value={form.bFresh} onChange={(e) => patch({ bFresh: e.target.value })} />
          </div>
        </div>
      </section>

      <section className="card space-y-3">
        <div>
          <div className="text-sm font-bold text-ink">不出貨日期</div>
          <div className="text-xxs text-muted mt-0.5">國定假日、工廠休息日等;這些日子不算工作天,日期推算會自動跳過。</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="input w-48"
            type="date"
            value={holidayInput}
            onChange={(e) => setHolidayInput(e.target.value)}
          />
          <button onClick={addHoliday} className="btn-secondary text-sm py-2">加入</button>
        </div>
        {form.holidays.length === 0 ? (
          <div className="text-xs text-muted">目前沒有設定不出貨日期。</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {form.holidays.map((h) => (
              <span
                key={h}
                className="inline-flex items-center gap-1 bg-cream border border-line rounded-full pl-3 pr-2 py-1 text-sm"
              >
                {h}({weekdayLabel(h)})
                <button
                  onClick={() => patch({ holidays: form.holidays.filter((x) => x !== h) })}
                  className="text-muted hover:text-coral"
                  aria-label={`移除 ${h}`}
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
        )}
      </section>

      <section className="card space-y-3">
        <div>
          <div className="text-sm font-bold text-ink">提醒與物流</div>
        </div>
        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={form.reminder} onChange={(e) => patch({ reminder: e.target.checked })} />
          下單前一天推播提醒到管理員 LINE 群組
        </label>
        <div>
          <label className="label">預設物流</label>
          <select className="input w-full max-w-xs" value={form.carrier} onChange={(e) => patch({ carrier: e.target.value })}>
            <option value="">不預設</option>
            {CARRIER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </section>

      <div className="flex justify-end">
        <button onClick={save} className="btn-primary" disabled={busy}>
          {busy ? "儲存中…" : "儲存設定"}
        </button>
      </div>

      <section className="card space-y-3">
        <div>
          <div className="text-sm font-bold text-ink">提醒內容預覽</div>
          <div className="text-xxs text-muted mt-0.5">以最近一個還沒下單的批次為準,依已儲存的設定計算;預覽不會推播。</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={loadPreview} className="btn-secondary text-sm py-2" disabled={previewLoading}>
            {previewLoading ? "載入中…" : "預覽提醒內容"}
          </button>
          <button onClick={pushTest} className="btn-secondary text-sm py-2" disabled={pushing}>
            {pushing ? "推播中…" : "推一則測試到管理群"}
          </button>
        </div>
        {preview && <ReminderPreview s={preview} />}
      </section>
    </div>
  );
}

function ReminderPreview({ s }: { s: SupplyReminderSummary }) {
  const check = s.raw_check;
  const next = s.next_batch;
  return (
    <div className="border border-line rounded-lg p-4 bg-cream/40 text-sm space-y-1.5">
      <div className="font-medium text-ink">
        {fmtMDWeek(s.order_date)} 下單批次
        <span className="ml-2 text-xs font-normal text-muted">
          到貨 {fmtMD(s.arrival_date, true)} · 會員出貨 {fmtMD(s.ship_date, true)}
        </span>
      </div>
      <div>
        名單 {s.line_count} 位 · {s.ordered ? `已下單 ${qtyText(s.ordered)}` : `建議下單 ${qtyText(s.suggested)}`}
      </div>
      {check && check.status !== "deducted" && (
        <div>
          需要原料 {fmtNum(s.raw_needed_grams)} 克 · 目前結餘 {fmtNum(s.raw_balance_grams)} 克 ·{" "}
          {check.status === "short" ? (
            <span className="text-coral">不足 差 {fmtNum(check.short_grams)} 克</span>
          ) : (
            <span className="text-primary">足夠</span>
          )}
        </div>
      )}
      {check?.status === "deducted" && <div>原料已在下單時扣除</div>}
      <div className={s.wallet_short_count + s.missing_address_count > 0 ? "text-coral" : "text-muted"}>
        存摺不足 {s.wallet_short_count} 位 · 缺地址 {s.missing_address_count} 位
      </div>
      {next && (
        <div className="text-xs text-muted pt-1 border-t border-line">
          下一批 {fmtMD(next.order_date, true)}:
          {next.raw_check?.status === "short" ? (
            <span className="text-coral"> 原料不足,差 {fmtNum(next.raw_check.short_grams)} 克</span>
          ) : (
            " 原料足夠"
          )}
        </div>
      )}
    </div>
  );
}
